import { isEmpty, trim } from "lodash";

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code (default: 'USD')
 * @returns Formatted currency string
 */
function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format a number as percentage
 * @param value - The value to format (e.g., 0.1 for 10% or use as number 10)
 * @param decimals - Number of decimal places (default: 1)
 * @param isRatio - If true, treats value as ratio (0.1 = 10%), if false treats as number (10 = 10%)
 * @returns Formatted percentage string
 */
function formatPercentage(value: number, decimals = 1, isRatio = false): string {
  if (isRatio) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number with locale-specific formatting (no abbreviations)
 * @param num - The number to format
 * @param locale - The locale string (default: 'en-US')
 * @returns Formatted number string with locale-specific formatting
 * @example
 * formatNumber(1234) // "1,234"
 * formatNumber(1234567) // "1,234,567"
 */
function formatNumber(num: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(num);
}

/**
 * Format a large number with abbreviations (K, M, B)
 * @param num - The number to format
 * @returns Formatted number string with abbreviation
 */
function formatCompactNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
}

/**
 * Format large numbers with K/M/B abbreviations (alternative implementation)
 * @param value - The number to format
 * @returns Formatted number string with abbreviation
 */
function formatNumberWithAbbr(value: number): string {
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
}


/**
  * Normalizes a numeric string by removing non-numeric characters and formatting it properly.
  *
  * @param {string} raw - The raw input string to normalize
  * @returns {string} A normalized numeric string with the following properties:
  *   - Only contains digits and at most one decimal point
  *   - Leading zeros are removed (except for values less than 1)
  *   - Decimal places are limited to 2 digits
  *   - Returns empty string if input is empty or whitespace-only
  *
  * @example
  * normalizeNumericString('  $1,234.567  ') // returns '1234.56'
  * normalizeNumericString('00123.4') // returns '123.4'
  * normalizeNumericString('0.99') // returns '0.99'
  * normalizeNumericString('abc') // returns ''
  * normalizeNumericString('  ') // returns ''
  */
const normalizeNumericString = (raw: string): string => {
  const trimmed = trim(raw);
  if (isEmpty(trimmed)) return '';

  // Remove non-numeric characters except dots, then remove duplicate dots
  const cleaned = trimmed.replace(/[^\d.]/g, '').replace(/\.(?=.*\.)/g, '');

  const [intPart = '', decimalPart = ''] = cleaned.split('.');

  // Remove leading zeros but keep at least one digit
  const normalizedInt = intPart.replace(/^0+(?=\d)/, '') || '0';

  // Limit decimal places to 2
  const normalizedDecimal = decimalPart.slice(0, 2);

  return normalizedDecimal ? `${normalizedInt}.${normalizedDecimal}` : normalizedInt;
};

// Namespace export for convenient usage: numberUtil.formatCurrency()
export const numberUtil = {
  formatCurrency,
  formatPercentage,
  formatNumber,
  formatCompactNumber,
  formatNumberWithAbbr,
  normalizeNumericString
} as const;

